/**
 * Shared pure-math scene-geometry builder for HATCH (DEFUSE Direction-3 —
 * clone elimination).
 *
 * Takes the ALREADY-PARSED HATCH primitive inputs (boundary `HatchBoundaryPath[]`,
 * pattern name, pattern scale, pattern angle IN DEGREES, solid flag, elevation,
 * and an ALREADY-RESOLVED `SceneGradient | undefined`) and returns the
 * `{ geometry, bounds }` pair that BOTH the legacy `../hatchToRenderable.ts`
 * (reads the `EntityPreview.properties` string bag) and the typed
 * `../typed/hatchFromEntity.ts` (reads the canonical `HatchEntity` fields) feed
 * into `buildRenderableShell`.
 *
 * # Why this module exists
 *
 * The legacy adapter and its typed sibling are byte-identical AFTER value
 * extraction — the SAME `walkHatchBoundary` + the SAME scene-side
 * `HatchLoopEmitter` (line → 2 pts, arc / ellipticArc → 33 samples, first-vertex
 * dedup, closing-duplicate pop, `< 3` drop, `pathTypeFlag & 1` → exterior) + the
 * SAME `kind: "hatch"` SceneGeometry assembly + the SAME `boundsFromPoints` AABB.
 * That is one semantic truth (the HATCH render-geometry math) — it must NOT be
 * copied between the two paths. It lives here ONCE; the two adapters differ ONLY
 * in how they derive the inputs (the value source).
 *
 * # Why the gradient is pre-resolved (not re-resolved here)
 *
 * The builder does NOT re-resolve the gradient ramp. Each adapter resolves the
 * `SceneGradient` in its own value-extraction step (legacy JSON-parses the
 * `__hatch_gradient` bag channel; typed calls `resolveGradientRamp` on the model
 * `gradient`) and passes the already-resolved `SceneGradient | undefined`. The
 * builder just SPREAD-omits it (`...(gradient !== undefined ? { gradient } : {})`)
 * so a non-gradient hatch keeps the field `undefined` — a clean discriminated
 * union member. This keeps the gradient resolution at the value source (where the
 * two paths genuinely differ) and avoids a `dxfGeometry → scene3d` dependency
 * edge for the typed path.
 *
 * # No earcut here (golden-parity)
 *
 * This builder emits `HatchLoop[]` ONLY — it does NOT earcut. The legacy adapter
 * never earcut at adapter time either (earcut runs later in `buildHatchFillMesh`).
 * Introducing an earcut call would break golden-parity.
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`.
 */

import type {
  FilledRegionLoop,
  HatchBoundaryPath,
  HatchEdgeCurve,
  HatchGradient,
  HatchLoopEmitter,
} from "@dxf-ai-editor/drawing-model";
import {
  renderedBoundaryLoopIndices,
  walkHatchBoundary,
} from "@dxf-ai-editor/drawing-model";

import { rgbToHex } from "../../../color/rgbCodec";
import { applyOffset } from "../../coordinateOrigin";
import { resolveGradientRamp } from "../../hatch/gradientRamp";
import { SCENE_STOP_RESOLVERS } from "../../hatch/sceneGradientResolvers";
import type {
  HatchLoop,
  SceneGeometry,
  SceneGradient,
  Vec3,
} from "../../types";
import { boundsFromPoints } from "../adapterContext";
import type { SceneGeometryResult } from "../sceneGeometryBuilders";

/**
 * Baseline tessellation count for arc / elliptic-arc edges of a hatch
 * boundary. Pinned to 32 segments because (a) hatches rarely carry
 * tightly-curved edges and (b) earcut runtime is dominated by vertex
 * count — going higher costs more than the visual win. (Verbatim from the
 * previous legacy-local copy — the byte-identity guard for slice 1b.)
 */
const HATCH_ARC_SEGMENT_COUNT = 32;

/** Inputs the builder needs — all already extracted from the value source. */
export interface HatchGeometryInput {
  /** Already-parsed boundary loops (legacy: JSON.parse of the bag; typed:
   *  `entity.boundaryPaths` straight off the model — same plain-data shape). */
  boundaryPaths: readonly HatchBoundaryPath[];
  /** Pattern name (DXF group 2). Default "ANSI31" applied by the caller. */
  patternName: string;
  /** Pattern scale (DXF group 41). Plain finite-or-default (NOT positive-clamped). */
  patternScale: number;
  /**
   * Pattern angle IN DEGREES (DXF group 52). The builder does the SINGLE
   * deg → rad conversion — callers pass degrees, NOT radians (no double-convert).
   */
  patternAngleDeg: number;
  /** Solid fill flag (already OR-ed with `patternName === "SOLID"` by the caller). */
  solid: boolean;
  /** Hatch elevation (DXF group 30) used as the per-vertex Z lift. */
  elevation: number;
  /**
   * Already-resolved gradient ramp, or `undefined` for a non-gradient hatch
   * (the field is then OMITTED from the geometry). Each adapter resolves this in
   * its own value-extraction step; the builder does NOT re-resolve.
   */
  gradient: SceneGradient | undefined;
}

/**
 * Build the `kind: "hatch"` SceneGeometry + AABB from the already-extracted
 * HATCH inputs. Walks the boundary via the shared `walkHatchBoundary` with the
 * scene-side emitter (one truth), forces the pattern name to "SOLID" when solid,
 * applies the single deg → rad pattern-angle conversion, and spread-omits the
 * gradient when absent. Bounds = `boundsFromPoints` over every loop vertex, or a
 * degenerate point `{0,0,0,0}` when there are no vertices (empty boundary).
 */
export function buildHatchSceneGeometry(
  input: HatchGeometryInput,
): SceneGeometryResult {
  const {
    boundaryPaths,
    patternName,
    patternScale,
    patternAngleDeg,
    solid,
    elevation,
    gradient,
  } = input;

  // Did the RAW source boundary TAG any EXTERIOR path (`pathTypeFlag` bit 0)?
  // Computed from `boundaryPaths` — the RAW input, BEFORE `walkHatchBoundary`
  // drops <3-vertex loops — so a tagged exterior that DEGENERATES (single edge →
  // <2 usable verts → dropped by the emitter) is still recorded here. Re-deriving
  // this from the post-walk `loops` would read FALSE (the degenerate exterior is
  // gone), firing the no-exterior fallback and promoting an interior loop to a
  // fake exterior fill (PR #410 P3 / @codex). This is the single tagged-degenerate
  // signal every consumer reads (render tessellator, these bounds, raycast
  // distance, window-select); it mirrors the drawing-model pick's `rawHadExterior`
  // (`distanceToHatchBoundary` re-walks the raw paths to recover the same bit).
  const hadTaggedExterior = boundaryPaths.some(
    (p) => ((p.pathTypeFlag ?? 0) & 1) !== 0,
  );

  const loops: HatchLoop[] = walkHatchBoundary(
    boundaryPaths,
    createSceneSegEmitter(elevation),
  );

  // Bounds must cover EXACTLY what the renderer draws — not every loop vertex.
  // `tessellateHatchLoops` emits only the RENDERED loops (every exterior + each
  // OWNED hole); an UNOWNED orphan loop (an interior loop outside every exterior,
  // multi-island regime) is DROPPED and renders nothing. Including its vertices
  // here would inflate the renderable bbox over invisible geometry, so the
  // identity/window-select bbox would span empty space (PR #410 P2). We bound only
  // the loops `renderedBoundaryLoopIndices` (the SHARED one-truth the tessellator
  // and the picks use) reports as rendered. `hadTaggedExterior` (the RAW-source bit
  // above) gates the no-exterior fallback: a tagged exterior that degenerated
  // leaves no usable exterior here → `renderedBoundaryLoopIndices` returns [] →
  // empty render → degenerate bbox, matching the empty fill.
  const usableLoops: FilledRegionLoop[] = loops.map((loop) => ({
    vertices: loop.vertices,
    isHole: loop.kind === "interior",
  }));
  const renderedIdx = renderedBoundaryLoopIndices(usableLoops, hadTaggedExterior);
  const renderedVertices: Vec3[] = [];
  for (const i of renderedIdx) {
    for (const v of loops[i]!.vertices) renderedVertices.push(v);
  }
  const bounds =
    renderedVertices.length > 0
      ? boundsFromPoints(renderedVertices)
      : { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  const geometry: SceneGeometry = {
    kind: "hatch",
    loops,
    patternName: solid ? "SOLID" : patternName,
    patternScale,
    patternAngle: (patternAngleDeg * Math.PI) / 180,
    solid,
    hadTaggedExterior,
    // OMIT when absent (non-gradient hatch) so the property stays `undefined`,
    // not an explicit `undefined` value — keeps the type a clean discriminated
    // union member for non-gradient hatches.
    ...(gradient !== undefined ? { gradient } : {}),
  };

  return { geometry, bounds };
}

/**
 * Build the scene-side {@link HatchLoopEmitter} consumed by the shared
 * {@link walkHatchBoundary} (slice 1b — single boundary-walk truth across the
 * PDF and scene render pipes). The walk converts every edge to the neutral
 * parametric {@link HatchEdgeCurve}; this emitter projects it into a polygonal
 * `HatchLoop` by sampling each curve at {@link HATCH_ARC_SEGMENT_COUNT}
 * segments and applying the per-vertex origin offset + elevation.
 *
 * Behaviour preserved verbatim from the previous legacy-local copy:
 *   - A LINE edge expands to its 2 endpoints (`toPolyline(1)`); an arc /
 *     ellipticArc edge expands to {@link HATCH_ARC_SEGMENT_COUNT}+1 samples
 *     (`toPolyline(32)` = 33 points).
 *   - The first vertex of every subsequent edge is dropped when it coincides
 *     (within 1e-9) with the previous edge's last vertex — keeps the loop a
 *     simple polygon for earcut.
 *   - The closing duplicate (last vertex == first) is popped: earcut treats the
 *     last→first edge as implicit and a duplicate would create a zero-area
 *     sliver.
 *   - `< 3` resulting vertices → the loop is dropped (`null`).
 *   - `pathTypeFlag` bit 0 (= 1, external) → `exterior`, else `interior`.
 *
 * A `start == end` arc / ellipticArc boundary edge renders as a FULL CIRCLE /
 * ELLIPSE (the canonical `normalizeSweep`'s `sweep === 0 → ±2π` full-circle
 * guard inside `edgeToCurve`). BOTH adapters inherit this identically by calling
 * `walkHatchBoundary` — no special handling on either path.
 *
 * Owning this emitter here (one truth) is exactly why both adapters produce
 * vertex-identical loops.
 */
function createSceneSegEmitter(
  elevation: number,
): HatchLoopEmitter<HatchLoop> {
  let vertices: Vec3[] = [];
  let lastVertex: Vec3 | null = null;

  return {
    beginLoop() {
      vertices = [];
      lastVertex = null;
    },
    // `_isFirstEdge` (from HatchLoopEmitter) is intentionally ignored: the scene
    // join policy dedups against the previous vertex regardless of edge order.
    emitEdge(curve: HatchEdgeCurve, _isFirstEdge: boolean) {
      // A line samples to its 2 endpoints; a curved edge to 32 segments.
      // (Old `expandEdge`: 2 pts for a line, 33 for an arc.) The neutral curve
      // samples in 2D; lift to offset-world space with the elevation as Z,
      // matching the old per-vertex `applyOffset({…, z})`.
      const segmentCount = curve.kind === "line" ? 1 : HATCH_ARC_SEGMENT_COUNT;
      const samples = curve
        .toPolyline(segmentCount)
        .map((p) => applyOffset({ x: p.x, y: p.y, z: elevation }));
      if (samples.length === 0) return;
      // Drop the first sample if it matches the previous edge's last vertex
      // (within 1e-9). Keeps the loop a simple polygon for earcut.
      let startIdx = 0;
      const first = samples[0];
      if (
        lastVertex !== null &&
        first !== undefined &&
        pointsEqual(lastVertex, first)
      ) {
        startIdx = 1;
      }
      for (let i = startIdx; i < samples.length; i++) {
        const v = samples[i];
        if (v !== undefined) vertices.push(v);
      }
      lastVertex = samples[samples.length - 1] ?? lastVertex;
    },
    endLoop(path: HatchBoundaryPath): HatchLoop | null {
      // Drop the closing duplicate if the loop closes back to the starting
      // vertex — earcut treats the first-vertex / last-vertex edge as implicit
      // and a duplicate would create a zero-area sliver.
      if (vertices.length >= 2) {
        const first = vertices[0];
        const last = vertices[vertices.length - 1];
        if (first && last && pointsEqual(first, last)) {
          vertices.pop();
        }
      }
      if (vertices.length < 3) return null;
      const kind: "exterior" | "interior" =
        (path.pathTypeFlag ?? 0) & 1 ? "exterior" : "interior";
      return { kind, vertices: vertices.slice(), closed: true };
    },
  };
}

function pointsEqual(a: Vec3, b: Vec3): boolean {
  return (
    Math.abs(a.x - b.x) < 1e-9 &&
    Math.abs(a.y - b.y) < 1e-9 &&
    Math.abs(a.z - b.z) < 1e-9
  );
}

// ── Typed gradient resolution (DEFUSE Direction-3) ──────────────────────────
//
// The TYPED hatch adapter (`../typed/hatchFromEntity.ts`) reads the canonical
// `HatchEntity.gradient` and must derive the SAME `SceneGradient` the legacy
// path produces (resolved in `dxfGeometry.resolveSceneGradient`, JSON-encoded
// into the `__hatch_gradient` bag channel). Both paths call the SAME shared
// `resolveGradientRamp` core with the SAME scene-faithful resolvers
// (`SCENE_STOP_RESOLVERS`) + `rgbToHex` codec — the one truth lives in
// `../../hatch/sceneGradientResolvers.ts` (a scene3d leaf, so the typed path
// needs no `dxfGeometry → scene3d` edge) — and the SAME field-by-field
// `GradientRampSpec → SceneGradient` mapping. Output is byte-identical to
// `resolveSceneGradient` (verified by the golden-parity test).

/**
 * Resolve a typed {@link HatchGradient} into the {@link SceneGradient} the hatch
 * geometry carries — the typed-path one-truth mirroring
 * `dxfGeometry.resolveSceneGradient`. Returns `undefined` for a non-gradient
 * hatch (`gradient` absent or `kind !== 1`) so the geometry's `gradient` field
 * is OMITTED, matching the producer's `null` (channel absent) on the legacy path.
 */
export function resolveTypedSceneGradient(
  gradient: HatchGradient | undefined,
): SceneGradient | undefined {
  if (gradient === undefined || gradient.kind !== 1) return undefined;
  // Entity fallback colour: gradient stops in real AutoCAD always carry their
  // own colour, so a defensive black fallback is sufficient (only hit by a stop
  // missing both aci + trueColor, which AutoCAD never writes). Identical to the
  // legacy `resolveSceneGradient` fallback.
  const ramp = resolveGradientRamp(
    gradient,
    { r: 0, g: 0, b: 0 },
    SCENE_STOP_RESOLVERS,
  );
  return {
    name: ramp.name,
    shadingType: ramp.shadingType,
    inverse: ramp.inverse,
    symmetric: ramp.symmetric,
    curved: ramp.curved,
    rotation: ramp.rotation,
    shift: ramp.shift,
    color0Hex: rgbToHex(ramp.color0),
    color1Hex: rgbToHex(ramp.color1),
  };
}
